import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import styles from "./styles/BlogPage.module.css";
import { Helmet } from "react-helmet";
import { defaultAxios } from "./apis";

const BlogPage = () => {
  const [availableCategories, setAvailableCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [showMoreDropdown, setShowMoreDropdown] = useState(false);

  const [articles, setArticles] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(9);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const query = searchParams.get("query") || "";
  const urlPage = Number(searchParams.get("page")) || 1;

  const language = window.location.pathname.startsWith("/kr") ? "kr" : "en";

  useEffect(() => {
    setPage(urlPage);
  }, [urlPage]);

  const fetchBlogs = async () => {
    try {
      setIsLoading(true);
      const res = await defaultAxios.get(`/api/blogs/`, {
        params: {
          language,
          page,
          page_size: pageSize,
          categories: selectedCategories,
        },
        paramsSerializer: { indexes: null },
      });
      const { results } = res.data;
      setArticles(results);
      setHasMore(results.length === pageSize);
    } catch (err) {
      console.error("Failed to load blogs", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogs();
  }, [language, page, selectedCategories]);

  useEffect(() => {
    const newParams = new URLSearchParams(searchParams);
    if (page === 1) {
      newParams.delete("page");
    } else {
      newParams.set("page", page);
    }
    setSearchParams(newParams);
  }, [page]);

  useEffect(() => {
    defaultAxios
      .get(`/api/blog-categories/`)
      .then((res) => {
        const data = res.data?.categories || [];
        setAvailableCategories(
          data.sort((a, b) => a.name.localeCompare(b.name)),
        );
      })
      .catch((err) => console.error("Failed to load categories", err));
  }, []);

  const handleCategoryClick = (catName) => {
    setSelectedCategories((prev) =>
      prev.includes(catName)
        ? prev.filter((c) => c !== catName)
        : [...prev, catName],
    );
    searchParams.delete("page");
    setSearchParams(searchParams);
    setPage(1);
  };

  useEffect(() => {
    const handleClickOutside = () => setShowMoreDropdown(false);
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const visibleCategories = availableCategories.slice(0, 5);
  const hiddenCategories = availableCategories.slice(5);
  const hasHiddenSelected = hiddenCategories.some((cat) =>
    selectedCategories.includes(cat.name),
  );

  const filteredArticles = query
    ? articles.filter(
        (article) =>
          article.title.toLowerCase().includes(query.toLowerCase()) ||
          article.excerpt.toLowerCase().includes(query.toLowerCase()),
      )
    : articles;

  const goNext = () => {
    if (hasMore) setPage((p) => p + 1);
  };
  const goPrev = () => {
    if (page > 1) setPage((p) => p - 1);
  };

  return (
    <div className={styles.blogContainer}>
      <Helmet>
        <title>Xenorize</title>
        <meta
          name="description"
          content="Get the latest news, tips, and insights from Xenorize. Explore trading education, crypto updates, and in-depth market analysis—all in one place."
        />
        <meta
          name="keywords"
          content="market analytics, crypto education, trading news, Xenorize blog, market insights"
        />
        <meta
          property="og:title"
          content="Xenorize Digest | Market Analysis, Crypto Updates & Education"
        />
        <link
          rel="canonical"
          href={`https://xenorize.com${language === "kr" ? "/kr" : ""}/blog/`}
        />
      </Helmet>

      <h1 className={styles.blogTitle}>Xenorize Blog</h1>
      <p className={styles.blogIntro}>
        News, tips and insights from our team. Explore trading education, crypto
        updates, and market analysis.
      </p>

      <div className={styles.filterContainer}>
        {visibleCategories.map((cat) => (
          <button
            key={cat.id}
            className={`${styles.filterButton} ${selectedCategories.includes(cat.id) ? styles.active : ""}`}
            onClick={() => handleCategoryClick(cat.id)}
          >
            {cat.name}
          </button>
        ))}

        {hiddenCategories.length > 0 && (
          <div className={styles.moreCategoriesWrapper}>
            <button
              className={`${styles.filterButton} ${styles.moreButton} ${hasHiddenSelected ? styles.active : ""}`}
              onClick={(e) => {
                e.stopPropagation();
                setShowMoreDropdown(!showMoreDropdown);
              }}
            >
              ...
            </button>
            {showMoreDropdown && (
              <div
                className={styles.moreDropdown}
                onClick={(e) => e.stopPropagation()}
              >
                {hiddenCategories.map((cat) => (
                  <button
                    key={cat.id}
                    className={`${styles.dropdownItem} ${selectedCategories.includes(cat.name) ? styles.active : ""}`}
                    onClick={() => handleCategoryClick(cat.name)}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div className={styles.articleGrid}>
        {isLoading ? (
          Array.from({ length: 9 }).map((_, i) => (
            <div
              key={i}
              className="w-full rounded-xl bg-white shadow-sm p-4 animate-pulse"
            >
              <div className="w-full h-40 bg-gray-300 rounded-lg"></div>
              <div className="h-4 bg-gray-300 rounded mt-4 w-3/4"></div>
              <div className="h-3 bg-gray-300 rounded mt-3 w-full"></div>
              <div className="h-3 bg-gray-300 rounded mt-2 w-5/6"></div>
            </div>
          ))
        ) : filteredArticles.length > 0 ? (
          filteredArticles.map((article) => (
            <div
              key={article.slug}
              className={styles.articleCard}
              onClick={() =>
                navigate(
                  `${language === "kr" ? "/kr" : ""}/digest/${article.slug}`,
                )
              }
            >
              <img
                src={
                  article.thumbnail?.trim()
                    ? article.thumbnail
                    : "/images/backgrounds/bg-no-img.svg"
                }
                alt={article.title}
                className={styles.thumbnail}
                onError={(e) => {
                  e.currentTarget.src = "/images/backgrounds/bg-no-img.svg";
                }}
              />
              <h2 className={styles.title}>{article.title}</h2>
              <p className={styles.excerpt}>{article.excerpt}</p>
            </div>
          ))
        ) : (
          <p className={styles.noResults}>No articles found.</p>
        )}
      </div>

      <div className="flex items-center justify-center gap-3 my-16 px-5">
        <button
          disabled={page <= 1}
          onClick={goPrev}
          className="px-6 py-3 bg-gradient-to-br from-[#302571] to-[#4a3a8f] border-2 border-[#302571] text-white rounded-lg font-semibold text-sm min-w-[110px] transition-all duration-300 hover:bg-[#302571] hover:text-white hover:shadow-lg disabled:opacity-30 disabled:cursor-not-allowed disabled:border-gray-300 disabled:text-gray-400 disabled:hover:bg-transparent disabled:hover:text-gray-400 disabled:hover:translate-y-0 disabled:hover:shadow-none sm:px-5 sm:py-2.5 sm:text-xs sm:min-w-[90px]"
        >
          Previous
        </button>
        <span className="px-5 py-3 text-[#302571] rounded-lg font-semibold text-sm min-w-[90px] text-center sm:px-4 sm:py-2.5 sm:text-xs sm:min-w-[75px]">
          Page {page}
        </span>
        <button
          disabled={!hasMore}
          onClick={goNext}
          className="px-6 py-3 bg-gradient-to-br from-[#302571] to-[#4a3a8f] border-2 border-[#302571] text-white rounded-lg font-semibold text-sm min-w-[110px] transition-all duration-300 hover:bg-[#302571] hover:text-white hover:shadow-lg disabled:opacity-30 disabled:cursor-not-allowed disabled:border-gray-300 disabled:text-gray-400 disabled:hover:bg-transparent disabled:hover:text-gray-400 disabled:hover:translate-y-0 disabled:hover:shadow-none sm:px-5 sm:py-2.5 sm:text-xs sm:min-w-[90px]"
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default BlogPage;
